import React from 'react';
import { Modal, Button, List, Typography, Avatar } from 'antd';
import { Link } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

const DialogFileDriver = ({ driversInFila, openModal, handleClose }) => {
  const theme = {
    colors: {
      background: '#F9FAFB',
      surface: '#FFFFFF',
      primary: '#012442',
      primarySoft: 'rgba(1, 36, 66, 0.08)',
      primaryBorder: 'rgba(1, 36, 66, 0.12)',
      textPrimary: '#1E2124',
      textSecondary: 'rgba(30, 33, 36, 0.72)',
      highlight: '#FF3100',
      highlightSoft: 'rgba(255, 49, 0, 0.10)',
      white: '#FFFFFF',
    },
    typography: {
      fontFamily:
        "'Airbnb Cereal VF', 'Circular', -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', sans-serif",
      weightRegular: 400,
      weightSemibold: 600,
      titleSize: '1rem',
      titleLineHeight: '1.25rem',
      bodySize: '0.875rem',
      bodyLineHeight: '1.25rem',
      captionSize: '0.75rem',
      captionLineHeight: '1rem',
      letterSpacing: 'normal',
    },
    spacing: {
      xxs: 4,
      xs: 8,
      sm: 12,
      md: 16,
      lg: 24,
      xl: 32,
    },
    radius: {
      small: 8,
      medium: 12,
      large: 16,
    },
    elevation: {
      base: '0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)',
    },
    motion: {
      transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
      iconTransition: 'transform 0.1s cubic-bezier(0.2, 0, 0, 1)',
    },
  };

  const getTimestampDate = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    if (timestamp instanceof Date) return timestamp;
    const seconds =
      typeof timestamp.seconds === 'number'
        ? timestamp.seconds
        : timestamp._seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
    return null;
  };

  const formatTimestamp = (timestamp) => {
    const date = getTimestampDate(timestamp);
    return date ? date.toLocaleString('pt-BR') : '--';
  };

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm,
            fontFamily: theme.typography.fontFamily,
          }}
        >
          <div
            style={{
              width: 6,
              height: 24,
              borderRadius: 999,
              background: theme.colors.highlight,
              boxShadow: theme.elevation.base,
            }}
          />
          <span
            style={{
              color: theme.colors.primary,
              fontWeight: theme.typography.weightSemibold,
              fontSize: theme.typography.titleSize,
              lineHeight: theme.typography.titleLineHeight,
              letterSpacing: theme.typography.letterSpacing,
            }}
          >
            Motoristas na Fila
          </span>
        </div>
      }
      open={openModal}
      onCancel={handleClose}
      centered
      styles={{
        content: {
          backgroundColor: theme.colors.background,
          boxShadow: theme.elevation.base,
          borderRadius: theme.radius.large,
          padding: theme.spacing.lg,
          fontFamily: theme.typography.fontFamily,
          overflow: 'hidden',
        },
        header: {
          backgroundColor: theme.colors.background,
          borderBottom: `1px solid ${theme.colors.primaryBorder}`,
          marginBottom: theme.spacing.md,
          paddingBottom: theme.spacing.md,
          fontFamily: theme.typography.fontFamily,
        },
        body: {
          paddingTop: theme.spacing.xs,
          fontFamily: theme.typography.fontFamily,
        },
        footer: {
          borderTop: `1px solid ${theme.colors.primaryBorder}`,
          marginTop: theme.spacing.lg,
          paddingTop: theme.spacing.md,
          fontFamily: theme.typography.fontFamily,
        },
      }}
      footer={[
        <Button
          key="ok"
          type="primary"
          onClick={handleClose}
          style={{
            backgroundColor: theme.colors.primary,
            borderColor: theme.colors.primary,
            color: theme.colors.white,
            fontWeight: theme.typography.weightRegular,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.bodySize,
            lineHeight: theme.typography.bodyLineHeight,
            boxShadow: theme.elevation.base,
            borderRadius: theme.radius.medium,
            minWidth: 96,
            height: 40,
            transition: theme.motion.transition,
          }}
        >
          OK
        </Button>,
      ]}
    >
      {driversInFila.length < 1 ? (
        <div
          style={{
            background: theme.colors.surface,
            border: `1px solid ${theme.colors.primaryBorder}`,
            borderRadius: theme.radius.large,
            padding: theme.spacing.lg,
            boxShadow: theme.elevation.base,
          }}
        >
          <Typography.Text
            style={{
              display: 'block',
              color: theme.colors.textSecondary,
              fontWeight: theme.typography.weightRegular,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.bodySize,
              lineHeight: theme.typography.bodyLineHeight,
              textAlign: 'center',
            }}
          >
            Não há motorista em espera para esse frete no momento.
          </Typography.Text>
        </div>
      ) : (
        <div
          style={{
            background: theme.colors.surface,
            border: `1px solid ${theme.colors.primaryBorder}`,
            borderRadius: theme.radius.large,
            boxShadow: theme.elevation.base,
            overflow: 'hidden',
          }}
        >
          <List
            dataSource={driversInFila}
            split={false}
            renderItem={(item, index) => (
              <List.Item
                style={{
                  padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
                  borderBottom:
                    index !== driversInFila.length - 1
                      ? `1px solid ${theme.colors.primaryBorder}`
                      : 'none',
                  transition: theme.motion.transition,
                  background: theme.colors.surface,
                }}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: theme.colors.highlightSoft,
                        color: theme.colors.highlight,
                        border: `1px solid rgba(255, 49, 0, 0.18)`,
                        fontWeight: theme.typography.weightSemibold,
                        fontFamily: theme.typography.fontFamily,
                        boxShadow: theme.elevation.base,
                      }}
                    >
                      {item.driver_name?.[0]?.toUpperCase()}
                    </Avatar>
                  }
                  title={
                    <Typography.Text
                      style={{
                        color: theme.colors.textPrimary,
                        fontWeight: theme.typography.weightSemibold,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: theme.typography.bodySize,
                        lineHeight: theme.typography.bodyLineHeight,
                        margin: 0,
                      }}
                    >
                      {item.driver_name}
                    </Typography.Text>
                  }
                  description={
                    <span
                      style={{
                        display: 'inline-block',
                        marginTop: theme.spacing.xxs,
                        color: theme.colors.textSecondary,
                        fontWeight: theme.typography.weightRegular,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: theme.typography.captionSize,
                        lineHeight: theme.typography.captionLineHeight,
                      }}
                    >
                      Na fila de espera desde:{' '}
                      {formatTimestamp(item?.registrationInLineTime)}
                    </span>
                  }
                />

                <Link
                  to={'/driverList/' + item.driver_user}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: theme.radius.medium,
                    color: theme.colors.highlight,
                    background: theme.colors.highlightSoft,
                    border: `1px solid rgba(255, 49, 0, 0.16)`,
                    boxShadow: theme.elevation.base,
                    transition: `${theme.motion.transition}, ${theme.motion.iconTransition}`,
                    textDecoration: 'none',
                    flexShrink: 0,
                  }}
                  aria-label={`Editar motorista ${item.driver_name}`}
                >
                  <EditOutlinedIcon
                    style={{
                      color: theme.colors.highlight,
                      fontSize: 18,
                    }}
                  />
                </Link>
              </List.Item>
            )}
          />
        </div>
      )}
    </Modal>
  );
};

export default DialogFileDriver;
